/**
 * 地磅串口通信工具模块
 * 基于 Web Serial API 实现地磅实时数据读取
 * 采用直接读取 reader 方式（非管道流），确保连接稳定性和断开时的正确清理
 *
 * 使用方式：
 *   const reader = new WeighbridgeReader()
 *   reader.setOnWeightChange((weight, stable) => { ... })
 *   await reader.connect({ baudRate: 9600 })
 *   // ...
 *   reader.destroy()
 */

// ==================== 耀华连续模式(tF=0) 帧解析(纯函数, 便于单测) ====================
//
// 帧格式(12 字节定长):
//   [0] STX=0x02
//   [1] 符号位: 0x2B('+') / 0x2D('-')
//   [2..7] 6 位称量数据(ASCII '0'~'9', 高位在前)
//   [8] 小数位数(ASCII '0'~'4', 即 0~4 位小数)
//   [9] 异或校验高 4 位(ASCII hex)
//   [10] 异或校验低 4 位(ASCII hex)
//   [11] ETX=0x03
// 异或校验范围 = [1..8](符号 + 6 位数据 + 小数位) 共 8 字节, 结果按高低 4 位转 ASCII hex.
// 示例: 02 2B 30 31 30 35 39 30 30 31 36 03  →  +010590, 小数0, 校验16  →  10590

const STX = 0x02;
const ETX = 0x03;

/**
 * 把 0~15 转成 ASCII hex 字符字节 ('0'~'9','A'~'F')
 * @param {number} nibble 0~15
 * @returns {number} ASCII 码
 */
function nibbleToAsciiHex(nibble) {
  return nibble < 10 ? 0x30 + nibble : 0x41 + (nibble - 10);
}

/**
 * 解析一帧完整的耀华连续模式数据
 * @param {Uint8Array|number[]} frame - 含 STX 和 ETX 的完整帧
 * @param {Object} [opts]
 * @param {boolean} [opts.verifyChecksum=true] - 是否校验异或位
 * @returns {number|null} 真实重量(已按小数位换算, 负值带负号); 非法帧返回 null
 */
export function parseYaohuaFrame(frame, opts = {}) {
  const { verifyChecksum = true } = opts;
  try {
    if (!frame || frame.length !== 12) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);

    // 帧头帧尾
    if (b[0] !== STX || b[11] !== ETX) return null;

    // 符号位
    const sign = b[1] === 0x2b ? 1 : b[1] === 0x2d ? -1 : 0;
    if (sign === 0) return null;

    // 6 位数据必须全是 ASCII 数字
    let dataStr = "";
    for (let i = 2; i <= 7; i++) {
      if (b[i] < 0x30 || b[i] > 0x39) return null;
      dataStr += String.fromCharCode(b[i]);
    }

    // 小数位
    if (b[8] < 0x30 || b[8] > 0x34) return null;
    const decimals = b[8] - 0x30;

    // 异或校验(范围 [1..8])
    if (verifyChecksum) {
      let xor = 0;
      for (let i = 1; i <= 8; i++) xor ^= b[i];
      const hi = nibbleToAsciiHex((xor >> 4) & 0x0f);
      const lo = nibbleToAsciiHex(xor & 0x0f);
      if (b[9] !== hi || b[10] !== lo) return null;
    }

    const raw = parseInt(dataStr, 10);
    if (isNaN(raw)) return null;

    const weight = raw / Math.pow(10, decimals);
    return sign < 0 ? -weight : weight;
  } catch (e) {
    return null;
  }
}

/**
 * 从字节缓冲区中切出所有完整的 STX...ETX 帧
 * @param {number[]} buf - 字节缓冲区(会被就地修改: 切完后保留最后一个未完成 STX 之后的残留)
 * @returns {number[][]} 完整帧数组(每帧是 number[])
 */
export function splitYaohuaFrames(buf) {
  const frames = [];
  let i = 0;
  let consumedAll = false; // 是否已消费到缓冲区末尾(正常退出)
  while (i < buf.length) {
    const stxIdx = buf.indexOf(STX, i);
    if (stxIdx === -1) {
      // i 之后再无 STX: 整段丢弃(纯噪声或帧间垃圾)
      consumedAll = true;
      break;
    }
    const etxIdx = buf.indexOf(ETX, stxIdx + 1);
    if (etxIdx === -1) {
      // 找到 STX 但还没收到 ETX: 保留 STX 起的残片, 等下次数据
      const residual = buf.slice(stxIdx);
      buf.length = 0;
      for (const x of residual) buf.push(x);
      return frames;
    }
    // STX...ETX 之间正好 10 字节内容 → 整帧 12 字节
    const frame = buf.slice(stxIdx, etxIdx + 1);
    frames.push(frame);
    i = etxIdx + 1;
  }
  if (consumedAll || i >= buf.length) {
    buf.length = 0;
  }
  return frames;
}

// ==================== 柯力 D 系列 D2+ 倒序连续模式帧解析(纯函数, 便于单测) ====================
//
// 帧格式(8~9 字节定长, 全 ASCII), 仪表约每 100~200ms 发送一帧:
//   [0..N-2] 显示字符串的倒序(N-1 个字符, 低位字符在前; 高位补 '0')
//   [N-1]    0x3D ('=', 帧分隔符)
// 柯力 D 系列(D2008/D2002/D11/D12/D30)通讯方式 TF=2(老D2+, 7 位数据 + '=' = 8 字节)和
// TF=3(新D2+, 8 位数据 + '=' = 9 字节)都是"整段倒序"格式: 显示 188.5 时线上发 "5.88100=",
// 即 hex 35 2E 38 38 31 30 30 3D。小数点以字符 '.'(0x2E) 内嵌在数据里, 倒序还原后定位;
// 负号 '-'(0x2D) 倒序后排在数据段最后一个字符(收发序末尾 = 反转后的最高位)。
// 耀华 XK3190 的 D2+ 兼容模式与此同族; 衡天 HT9800 是含尾随空格的变体,
// 由 ht9800-continuous 严格格式单独覆盖, 本解析器兼容 6~8 位数据段(含克隆仪表空格补位)。
// 示例: 35 2E 38 38 31 30 30 3D → 数据段 "5.88100" 反转 "00188.5" → 188.5 kg。
// 依据: 柯力《2008系列数字仪表通讯协议格式》《D2008与D12串口通讯协议说明》等官方手册。

const KELI_MIN_DATA_LEN = 6; // 数据段最短 6 字符
const KELI_MAX_DATA_LEN = 8; // 数据段最长 8 字符(新D2+)
const KELI_TAIL = 0x3d; // '='

/**
 * 柯力数据段字符合法性: 数字 / '.' / 空格(克隆补位) / '-'(仅允许收发序末尾 = 倒序后最高位)
 * @param {Uint8Array|number[]} body
 * @returns {boolean}
 */
function keliBodyValid(body) {
  if (!body || body.length < KELI_MIN_DATA_LEN || body.length > KELI_MAX_DATA_LEN) return false;
  let dots = 0;
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (c >= 0x30 && c <= 0x39) continue;
    if (c === 0x2e) {
      dots++;
      continue;
    }
    if (c === 0x20) continue;
    if (c === 0x2d && i === body.length - 1) continue;
    return false;
  }
  return dots <= 1;
}

/** 把"倒序数据段"还原成重量: 反转 → 去空格 → [符号]整数[.小数] 解析 */
function parseKeliReversedBody(body) {
  let disp = "";
  for (let i = body.length - 1; i >= 0; i--) disp += String.fromCharCode(body[i]);
  const str = disp.trim();
  const m = str.match(/^(-?)(\d+)(?:\.(\d*))?$/);
  if (!m) return null;
  const frac = m[3] === undefined || m[3] === "" ? "" : "." + m[3];
  const w = parseFloat(m[2] + frac);
  if (isNaN(w)) return null;
  return m[1] === "-" ? -w : w;
}

/**
 * 解析一帧柯力 D 系列 D2+ 倒序连续模式数据
 * @param {Uint8Array|number[]} frame - 含末尾 '=' 的完整帧
 * @returns {number|null} 重量(按帧内小数点字符换算); 非法帧返回 null
 */
export function parseKeliFrame(frame) {
  try {
    if (!frame) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);
    const dataLen = b.length - 1;
    if (dataLen < KELI_MIN_DATA_LEN || dataLen > KELI_MAX_DATA_LEN) return null;
    if (b[dataLen] !== KELI_TAIL) return null;
    const body = b.slice(0, dataLen);
    if (!keliBodyValid(body)) return null;
    return parseKeliReversedBody(body);
  } catch (e) {
    return null;
  }
}

/**
 * 从字节缓冲区中切出所有完整的柯力 D2+ 帧。
 * 对齐策略: 以 '=' 为锚点, 从长到短(8→6)向前找合法数据段窗口 — 数据字符不含 '=',
 * 相邻帧以 '=' 相隔, 长度优先即可在老/新 D2+ 两种帧长下正确切分。
 * 校验不过的 '=' 跳过; 无锚点时最多保留末尾 8 字节残片。
 * @param {number[]} buf - 字节缓冲区(会被就地修改: 切完后只保留末尾残片)
 * @returns {number[][]} 完整帧数组(每帧是 number[])
 */
export function splitKeliFrames(buf) {
  const frames = [];
  let i = 0;
  while (i < buf.length) {
    const eqIdx = buf.indexOf(KELI_TAIL, i);
    if (eqIdx === -1) {
      let start = buf.length - KELI_MAX_DATA_LEN;
      if (start < i) start = i;
      const residual = buf.slice(start);
      buf.length = 0;
      for (const x of residual) buf.push(x);
      return frames;
    }
    for (let len = KELI_MAX_DATA_LEN; len >= KELI_MIN_DATA_LEN; len--) {
      const start = eqIdx - len;
      if (start < i) continue; // 该长度越界(前面字节不足), 试更短的窗口
      if (keliBodyValid(buf.slice(start, eqIdx))) {
        frames.push(buf.slice(start, eqIdx + 1));
        break;
      }
    }
    i = eqIdx + 1;
  }
  buf.length = 0;
  return frames;
}

// ==================== 柯力 XK3101 连续方式1 帧解析(纯函数, 便于单测) ====================
//
// 帧格式(10 字节定长, 全 ASCII, 正序), XK3101 参数 F3.2=1(连续发送方式1):
//   [0]    0x3D ('=' 帧头)
//   [1..7] 7 位显示字符(高位在前, 前导补 '0'; 小数点为字符 '.'; 负数时 '-' 占最高位)
//   [8]    0x0D (CR)
//   [9]    0x0A (LF)
// 示例: 3D 30 31 32 33 34 2E 35 0D 0A → "=01234.5\r\n" → 1234.5 kg
//       3D 2D 31 32 33 34 2E 35 0D 0A → "=-1234.5\r\n" → -1234.5 kg
// 注意: XK3101 的连续方式是正序, 与 D 系列的 D2+ 倒序不是一套, 别张冠李戴。

const XK3101_FRAME_LEN = 10;
const XK3101_HEAD = 0x3d; // '='
const XK3101_CR = 0x0d;
const XK3101_LF = 0x0a;

/** XK3101 显示段合法性: 7 字符, '-' 只能在 [0], '.' 至多 1 个, 数字至少 4 位 */
function xk3101BodyValid(body) {
  if (!body || body.length !== 7) return false;
  let dots = 0;
  let digits = 0;
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (c >= 0x30 && c <= 0x39) {
      digits++;
      continue;
    }
    if (c === 0x2e) {
      dots++;
      continue;
    }
    if (c === 0x2d && i === 0) continue;
    return false;
  }
  return dots <= 1 && digits >= 4;
}

/**
 * 解析一帧柯力 XK3101 连续方式1 数据
 * @param {Uint8Array|number[]} frame - 10 字节完整帧
 * @returns {number|null} 重量(按帧内小数点换算); 非法帧返回 null
 */
export function parseXk3101Frame(frame) {
  try {
    if (!frame || frame.length !== XK3101_FRAME_LEN) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);
    if (b[0] !== XK3101_HEAD || b[8] !== XK3101_CR || b[9] !== XK3101_LF) return null;
    if (!xk3101BodyValid(b.slice(1, 8))) return null;
    let str = "";
    for (let i = 1; i <= 7; i++) str += String.fromCharCode(b[i]);
    const m = str.match(/^(-?)(\d+)(?:\.(\d+))?$/);
    if (!m) return null;
    const w = parseFloat(m[2] + (m[3] ? "." + m[3] : ""));
    if (isNaN(w)) return null;
    return m[1] === "-" ? -w : w;
  } catch (e) {
    return null;
  }
}

/**
 * 从字节缓冲区中切出所有完整的 XK3101 帧。
 * 对齐策略: 以 LF 为锚点向前找 10 字节窗口('=' 开头 + 7 位显示 + CR)。
 * @param {number[]} buf - 字节缓冲区(会被就地修改: 切完后只保留末尾残片)
 */
export function splitXk3101Frames(buf) {
  const frames = [];
  let i = 0;
  while (i < buf.length) {
    const lfIdx = buf.indexOf(XK3101_LF, i);
    if (lfIdx === -1) {
      let start = buf.length - (XK3101_FRAME_LEN - 1);
      if (start < i) start = i;
      const residual = buf.slice(start);
      buf.length = 0;
      for (const x of residual) buf.push(x);
      return frames;
    }
    const start = lfIdx - (XK3101_FRAME_LEN - 1);
    if (
      start >= i &&
      buf[start] === XK3101_HEAD &&
      buf[lfIdx - 1] === XK3101_CR &&
      xk3101BodyValid(buf.slice(start + 1, lfIdx - 1))
    ) {
      frames.push(buf.slice(start, lfIdx + 1));
    }
    i = lfIdx + 1;
  }
  buf.length = 0;
  return frames;
}

// ==================== 托利多(T800/8142 兼容)连续输出帧解析(纯函数, 便于单测) ====================
//
// 帧格式(18 字节定长), 柯力 D 系列 TF=4/5(兼容托利多 T800)与托利多 8142 连续输出同族:
//   [0]      0x02 (STX)
//   [1]      状态字A: bit0-2 = 小数位数(0~4), bit5 恒 1
//   [2]      状态字B: bit1 = 符号(0正/1负), bit2 = 超载, bit3 = 动态(不稳定)
//   [3]      状态字C: 固定 0x20
//   [4..9]   6 位毛重(ASCII '0'~'9', 高位在前, 无符号无小数点)
//   [10..15] 6 位皮重(ASCII)
//   [16]     0x0D (CR)
//   [17]     校验 CKS: 兼容三种常见算法 — 前 17 字节求和补码(含/不含 STX)或异或
// 重量 = 毛重 ÷ 10^小数位, 符号取状态字B bit1。示例帧见单测。

const TOLEDO_FRAME_LEN = 18;
const TOLEDO_CR = 0x0d;

/**
 * 解析一帧托利多连续输出数据
 * @param {Uint8Array|number[]} frame - 18 字节完整帧
 * @param {Object} [opts]
 * @param {boolean} [opts.verifyChecksum=true] - 是否校验 CKS(三种算法任一命中即通过)
 * @returns {number|null} 毛重 kg(已按小数位换算); 非法帧返回 null
 */
export function parseToledoFrame(frame, opts = {}) {
  const { verifyChecksum = true } = opts;
  try {
    if (!frame || frame.length !== TOLEDO_FRAME_LEN) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);
    if (b[0] !== STX || b[16] !== TOLEDO_CR) return null;
    for (let i = 4; i <= 15; i++) {
      if (b[i] < 0x30 || b[i] > 0x39) return null;
    }
    const decimals = b[1] & 0x07;
    if (decimals > 4) return null;
    if (verifyChecksum) {
      let sum = 0;
      let sumNoStx = 0;
      let xor = 0;
      for (let i = 0; i <= 16; i++) {
        sum += b[i];
        xor ^= b[i];
        if (i > 0) sumNoStx += b[i];
      }
      const cks = b[17];
      const okSum = ((sum + cks) & 0xff) === 0;
      const okSumNoStx = ((sumNoStx + cks) & 0xff) === 0;
      const okXor = xor === cks;
      if (!okSum && !okSumNoStx && !okXor) return null;
    }
    let gross = 0;
    for (let i = 4; i <= 9; i++) gross = gross * 10 + (b[i] - 0x30);
    const weight = gross / Math.pow(10, decimals);
    return (b[2] & 0x02) !== 0 ? -weight : weight;
  } catch (e) {
    return null;
  }
}

/**
 * 从字节缓冲区中切出所有完整的托利多帧。
 * 对齐策略: 以 STX(0x02) 为锚点, 要求其后第 16 字节为 CR 且 [4..15] 全为 ASCII 数字;
 * 不满足则跳过该 STX 继续找; 有 STX 但帧未收全时保留残片。
 * @param {number[]} buf - 字节缓冲区(会被就地修改: 切完后只保留末尾残片)
 */
export function splitToledoFrames(buf) {
  const frames = [];
  let i = 0;
  while (i < buf.length) {
    const stxIdx = buf.indexOf(STX, i);
    if (stxIdx === -1) {
      buf.length = 0; // 无 STX: 整段是噪声
      return frames;
    }
    if (stxIdx + TOLEDO_FRAME_LEN > buf.length) {
      const residual = buf.slice(stxIdx);
      buf.length = 0;
      for (const x of residual) buf.push(x);
      return frames;
    }
    if (buf[stxIdx + 16] === TOLEDO_CR) {
      let digitsOk = true;
      for (let k = stxIdx + 4; k <= stxIdx + 15; k++) {
        if (buf[k] < 0x30 || buf[k] > 0x39) {
          digitsOk = false;
          break;
        }
      }
      if (digitsOk) {
        frames.push(buf.slice(stxIdx, stxIdx + TOLEDO_FRAME_LEN));
        i = stxIdx + TOLEDO_FRAME_LEN;
        continue;
      }
    }
    i = stxIdx + 1; // 这个 STX 不是帧头(或数字段不符), 跳过继续找
  }
  buf.length = 0;
  return frames;
}

// ==================== 打印式 ASCII 连续帧解析(纯函数, 便于单测) ====================
//
// 帧格式(变长 ASCII 文本行, 以 CRLF 结尾) — 托利多 ST/GS 打印式 / 柯力 TF=7:
//   "ST,GS,+00188.5kg\r\n"
//    └ 状态: ST=稳定 US=不稳定 OL=超载
//       └ 毛净: GS=毛重 NT=净重 TR=皮重
// 重量字段为 [符号]整数[.小数], 单位 kg/g/t/lb(g/t/lb 自动换算成 kg)。

const STGS_LINE_RE = /^(ST|US|OL),(GS|NT|TR),([+-]?)(\d+)(?:\.(\d+))?\s*(kg|g|t|lb)?\s*$/i;

/**
 * 解析一行打印式 ASCII 称重数据(含末尾 CRLF 或不含均可)
 * @param {Uint8Array|number[]} frame - 一行完整数据
 * @returns {number|null} 重量 kg; 非法行/超载行(OL)返回 null
 */
export function parseStgsFrame(frame) {
  try {
    if (!frame) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);
    let str = "";
    for (let i = 0; i < b.length; i++) str += String.fromCharCode(b[i]);
    str = str.replace(/\r?\n$/, "").trim();
    const m = str.match(STGS_LINE_RE);
    if (!m) return null;
    if (m[1].toUpperCase() === "OL") return null; // 超载行没有可靠重量
    const num = parseFloat(m[4] + (m[5] ? "." + m[5] : ""));
    if (isNaN(num)) return null;
    let w = m[3] === "-" ? -num : num;
    const unit = (m[6] || "kg").toLowerCase();
    if (unit === "g") w /= 1000;
    else if (unit === "t") w *= 1000;
    else if (unit === "lb") w *= 0.45359237;
    return w;
  } catch (e) {
    return null;
  }
}

/**
 * 从字节缓冲区中切出所有完整文本行(以 LF 结尾)。
 * 切分器宽松(整行都交给 parse 校验), 无 LF 时保留末尾 ≤64 字节残片。
 * @param {number[]} buf - 字节缓冲区(会被就地修改: 切完后只保留末尾残片)
 */
export function splitStgsFrames(buf) {
  const frames = [];
  let i = 0;
  while (i < buf.length) {
    const lfIdx = buf.indexOf(0x0a, i);
    if (lfIdx === -1) {
      const residual = buf.length > 64 ? buf.slice(-64) : buf.slice();
      buf.length = 0;
      for (const x of residual) buf.push(x);
      return frames;
    }
    frames.push(buf.slice(i, lfIdx + 1));
    i = lfIdx + 1;
  }
  buf.length = 0;
  return frames;
}

// ==================== 志美/至美 PT650D 连续输出帧解析(纯函数, 便于单测) ====================
//
// 珠海志美(CHIMEI) PT650D 连续发送模式; 现场同名写作"至美"(同音), 按一家处理。
// 串口参数与其它协议都不同: 7 位数据位 + 偶校验 + 1 停止位(7-E-1), ASCII, 波特率 2400~19200(默认 9600)。
// 帧格式(手册 9-3-3, 18 字节定长文本行, CRLF 结尾), 实际输出样例:
//   "ST,GS,+   1234kg\r\n"
//    [1-2]   状态1: ST=稳定 US=不稳定 OL=超载
//    [3]     ','(0x2C)
//    [4-5]   状态2: GS=毛重 NT=净重
//    [6]     ','(0x2C)
//    [7]     极性 '+'/'-'
//    [8-14]  7 位重量字符(右对齐, 空位补空格, 小数点内嵌; 无小数点时 NO.8 为空格)
//    [15-16] 单位: kg / t(手册 2-4 另列出 lb)
//    [17-18] CR LF
// 与 stgs-continuous("ST,GS,+00188.5kg", 无补位)的区别: 符号后有空格补位 + 串口 7-E-1。
// 解析前对每个字节 &0x7F 容错校验位: 按 8-N-1 打开(波特率/协议探测场景)时,
// 7E1 帧的第 8 个数据位是偶校验位, 会随机把 bit7 置 1, 掩码后 ASCII 内容不变。

const PT650D_LINE_RE = /^(ST|US|OL),(GS|NT|TR),([+-]?)(\s*)(\d+)(?:\.(\d+))?\s*(kg|g|t|lb)?\s*$/i;

/**
 * 解析一行志美/至美 PT650D 连续输出数据(含末尾 CRLF 或不含均可)
 * @param {Uint8Array|number[]} frame - 一行完整数据
 * @returns {number|null} 重量 kg; 非法行/超载行(OL)返回 null
 */
export function parsePt650dFrame(frame) {
  try {
    if (!frame) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);
    let str = "";
    for (let i = 0; i < b.length; i++) str += String.fromCharCode(b[i] & 0x7f);
    str = str.replace(/\r?\n$/, "").trim();
    const m = str.match(PT650D_LINE_RE);
    if (!m) return null;
    if (m[1].toUpperCase() === "OL") return null; // 超载行没有可靠重量
    const num = parseFloat(m[5] + (m[6] ? "." + m[6] : ""));
    if (isNaN(num)) return null;
    let w = m[3] === "-" ? -num : num;
    const unit = (m[7] || "kg").toLowerCase();
    if (unit === "g") w /= 1000;
    else if (unit === "t") w *= 1000;
    else if (unit === "lb") w *= 0.45359237;
    return w;
  } catch (e) {
    return null;
  }
}

/**
 * PT650D 与打印式同为"变长文本行, LF 结尾", 切分逻辑与 splitStgsFrames 一致
 * (整行交给 parse 校验, 无 LF 时保留末尾 ≤64 字节残片)。
 */
export const splitPt650dFrames = splitStgsFrames;

// ==================== 衡天 HT9800 连续模式帧解析(纯函数, 便于单测) ====================
//
// 帧格式(8 字节定长, 全 ASCII), 仪表约每 100ms 发送一帧:
//   [0]    0x20 (空格, 帧头)
//   [1..6] 6 位称量数据(ASCII '0'~'9', 低位在前 = 整帧字节序反转)
//   [7]    0x3D ('=', 帧尾)
// HT9800 把显示串 "=NNNNNN "(等号 + 6 位重量 + 尾随空格)整帧倒序发送,
// 线上看到的字节是 " NNNNNN="(空格 + 6 位反序数字 + '=')。
// 把 [1..6] 反转回高位在前即得 6 位整数重量; 帧内无小数位信息(按整数 kg 处理),
// 符号位 '-'(0x2D) 只会出现在反转后的高位(即收发的 [6])。
// 示例: 20 35 32 32 30 30 30 3D → 反转 "=000225 " → 225 kg。
// 依据现场抓包逆向(1006 帧全部解析成功, 分度值 5), jinglunGo scale_protocol.go 有等价实现。

const HT9800_FRAME_LEN = 8;
const HT9800_HEAD = 0x20; // 空格
const HT9800_TAIL = 0x3d; // '='

/**
 * 校验 8 字节窗口的帧体: 低 5 位(收发序 [1..5])必须是数字,
 * 最高位(收发序 [6], 反转后的高位)允许数字或负号。
 * @param {Uint8Array|number[]} b - 完整 8 字节窗口
 * @returns {boolean}
 */
function ht9800BodyValid(b) {
  if (!b || b.length !== HT9800_FRAME_LEN) return false;
  for (let i = 1; i <= 5; i++) {
    if (b[i] < 0x30 || b[i] > 0x39) return false;
  }
  const msb = b[6];
  return (msb >= 0x30 && msb <= 0x39) || msb === 0x2d;
}

/**
 * 解析一帧完整的衡天 HT9800 连续模式数据
 * @param {Uint8Array|number[]} frame - 8 字节完整帧
 * @returns {number|null} 整数 kg(帧内无小数位信息, 负值带负号); 非法帧返回 null
 */
export function parseHT9800Frame(frame) {
  try {
    if (!frame || frame.length !== HT9800_FRAME_LEN) return null;
    const b = frame instanceof Uint8Array ? frame : new Uint8Array(frame);
    if (b[0] !== HT9800_HEAD || b[7] !== HT9800_TAIL) return null;
    if (!ht9800BodyValid(b)) return null;
    const sign = b[6] === 0x2d ? -1 : 1;
    // 收发序 [6] 是反转后的高位, [5..1] 依次是低位→高位, 反序累加还原
    let raw = b[6] === 0x2d ? 0 : b[6] - 0x30;
    for (let i = 5; i >= 1; i--) {
      raw = raw * 10 + (b[i] - 0x30);
    }
    return sign < 0 ? -raw : raw;
  } catch (e) {
    return null;
  }
}

/**
 * 从字节缓冲区中切出所有完整的 HT9800 帧
 * 对齐策略: 以 '=' 为锚点向前找 8 字节窗口(空格开头 + 6 位合法数据),
 * 校验不过的 '=' 直接跳过继续找下一个(噪声里可能混入 0x3D)。
 * 无锚点时最多保留末尾 7 字节残片(一帧最多还差 1 个 '=' 没到), 防噪声无限增长。
 * @param {number[]} buf - 字节缓冲区(会被就地修改: 切完后只保留末尾残片)
 * @returns {number[][]} 完整帧数组(每帧是 number[])
 */
export function splitHT9800Frames(buf) {
  const frames = [];
  let i = 0;
  while (i < buf.length) {
    const eqIdx = buf.indexOf(HT9800_TAIL, i);
    if (eqIdx === -1) {
      // 没有 '=': 末尾最多留 7 字节等下一帧补齐
      let start = buf.length - (HT9800_FRAME_LEN - 1);
      if (start < i) start = i;
      const residual = buf.slice(start);
      buf.length = 0;
      for (const x of residual) buf.push(x);
      return frames;
    }
    const start = eqIdx - (HT9800_FRAME_LEN - 1);
    if (start >= i && buf[start] === HT9800_HEAD && ht9800BodyValid(buf.slice(start, eqIdx + 1))) {
      frames.push(buf.slice(start, eqIdx + 1));
    }
    i = eqIdx + 1;
  }
  buf.length = 0;
  return frames;
}

// ==================== 协议注册表 + 离线协议检测(纯函数, 便于单测) ====================
//
// SCALE_PROTOCOL_PARSE_HANDLERS: 协议代号 → { split, parse }。
// WeighbridgeReader._dataReceived/_parseFrame 与协议检测 detectProtocolFromBuffer 共用这份注册表,
// 新增协议在这里注册一行即可(代号与 jinglunGo scale_protocol.go 保持一致)。

export const SCALE_PROTOCOL_PARSE_HANDLERS = {
  "yaohua-continuous": { split: splitYaohuaFrames, parse: (f) => parseYaohuaFrame(f) },
  "ht9800-continuous": { split: splitHT9800Frames, parse: parseHT9800Frame },
  "keli-continuous": { split: splitKeliFrames, parse: parseKeliFrame },
  "xk3101-continuous": { split: splitXk3101Frames, parse: parseXk3101Frame },
  "toledo-continuous": { split: splitToledoFrames, parse: (f) => parseToledoFrame(f) },
  "stgs-continuous": { split: splitStgsFrames, parse: parseStgsFrame },
  "pt650d-continuous": { split: splitPt650dFrames, parse: parsePt650dFrame },
};

// 协议 → 串口打开参数差异。只有 PT650D 特殊(7-E-1 偶校验), 其余协议都是默认 8-N-1。
// WeighbridgeReader.connect() 按 this._protocol 自动套用; 调用方显式传的 dataBits/parity 优先。
export const SCALE_PROTOCOL_SERIAL_PARAMS = {
  "pt650d-continuous": { dataBits: 7, parity: "even" },
};

// 协议检测优先级: 耀华最常见放首位, 其余按市场常见度排列(用户要求: 优先检测耀华, 其它都试)。
// ht9800 排在 keli 之前: 衡天帧(空格头 + 数字 + '=')同样满足柯力 D2+ 通用倒序解析器的
// 字符约束, 先试衡天可把"标签"判给更精确的协议(两个解析器读出的重量一致, 不影响读数)。
// pt650d 排在 stgs 之后: 无补位的打印式行两者都能解, 先试 stgs 保持存量标签不变;
// PT650D 的空格补位行只匹配 pt650d, 8-N-1 抓到的脏校验位行也只有 pt650d(有 0x7F 掩码)能解。
export const SCALE_PROTOCOL_DETECT_ORDER = [
  "yaohua-continuous",
  "ht9800-continuous",
  "keli-continuous",
  "xk3101-continuous",
  "toledo-continuous",
  "stgs-continuous",
  "pt650d-continuous",
];

/**
 * 重量合理性校验(独立函数, 协议检测与 WeighbridgeReader 共用同一套阈值)
 * @param {number} weight
 * @returns {boolean}
 */
export function isPlausibleWeightValue(weight) {
  if (typeof weight !== "number" || isNaN(weight)) return false;
  if (weight > 200000) return false; // > 200 吨: 明显荒谬
  if (weight < -1000) return false; // < -1 吨: 明显异常
  return true;
}

/**
 * 对一段抓到的原始字节跑全部协议解析器, 找出能稳定解出重量的协议(耀华优先)。
 * 判定标准: 该协议在缓冲区里能解析出 ≥2 帧合理重量(连续模式仪表 2 秒内至少发 2 帧,
 * 用 2 帧阈值过滤单字节噪声的偶然命中)。
 * @param {number[]|Uint8Array} rawBuf - 抓到的原始字节
 * @returns {{protocol:string, frames:number, weight:number}|null} 命中的协议; 全部失败返回 null
 */
export function detectProtocolFromBuffer(rawBuf) {
  if (!rawBuf || !rawBuf.length) return null;
  for (const proto of SCALE_PROTOCOL_DETECT_ORDER) {
    const h = SCALE_PROTOCOL_PARSE_HANDLERS[proto];
    if (!h) continue;
    const buf = Array.from(rawBuf); // 切帧器会就地修改, 用副本
    const frames = h.split(buf) || [];
    let valid = 0;
    let lastWeight = null;
    for (const f of frames) {
      const w = h.parse(f);
      if (w !== null && isPlausibleWeightValue(w)) {
        valid++;
        lastWeight = w;
      }
    }
    if (valid >= 2) return { protocol: proto, frames: valid, weight: lastWeight };
  }
  return null;
}

class WeighbridgeReader {
  constructor() {
    this.serialPort = null; // SerialPort 实例
    this.reader = null; // ReadableStreamDefaultReader
    this.serialOpen = false; // 串口是否打开
    this._connected = false; // 连接状态标识
    // [2026-07-15] 串口释放竞态修复:
    //   _readLoopDeferred: _readSerialData() 退出时 resolve; disconnect() 会 await 它,
    //                      确保"读取循环退出→关闭串口"这条链跑完, 再由 destroy 做后续 close (去重).
    //                      避免刷新/断开时 destroy 的 close 与读取循环尾部的 close 并发, 卡死 MOXA 驱动句柄.
    //   _portClosed: close() 去重标记, 保证一个实例生命周期内 serialPort.close() 只执行一次.
    this._readLoopDeferred = null;
    this._portClosed = false;
    // 解析协议(默认耀华连续模式), connect 时可由 options.protocol 覆盖
    this._protocol = "yaohua-continuous";

    // 数据解析
    this.textDecoder = new TextDecoder();
    this.serialDataBuffer = []; // 分包合并缓冲区
    this.serialMergeTimer = null; // 分包合并定时器
    this.recentWeights = []; // 近期重量记录（用于稳定性检测）

    // [2026-07-24] 数据超时 watchdog:
    //   页面刷新后自动连接(presetPort)可能"表面 open 成功但底层句柄降级"——连着却收不到任何数据;
    //   或者波特率不对——收到数据但全是乱码, 解析不出有效帧.
    //   耀华连续模式每 ~100ms 发一帧, DATA_TIMEOUT_MS 内无有效帧即触发 onDataTimeout,
    //   并带诊断信息 {rawDataReceived, validFrameReceived} 让上层区分"端口降级"与"波特率错误".
    this._dataWatchdogTimer = null;
    this._lastFrameAt = 0; // 最后一次解析出有效帧的时间
    this._rawDataReceived = false; // 本次连接期间是否收到过任何原始字节

    // 回调函数
    this.onWeightChange = null; // 重量变化回调: (weight: number, stable: boolean) => void
    this.onConnect = null; // 连接成功回调: () => void
    this.onDisconnect = null; // 断开回调: () => void
    this.onError = null; // 错误回调: (msg: string) => void
    this.onRawData = null; // 原始数据回调: (hexStr: string, textStr: string) => void
    this.onDataTimeout = null; // [2026-07-24] 数据超时回调: (info: {rawDataReceived: boolean}) => void

    // 串口配置默认值
    this._defaultOptions = {
      baudRate: 9600,
      dataBits: 8,
      stopBits: 1,
      parity: "none",
      bufferSize: 2048,
      flowControl: "none",
    };

    // 分包合并超时（毫秒）
    this.mergeTimeout = 50;
  }

  /** [2026-07-24] 有效帧超时阈值: 耀华连续模式 ~10帧/秒, 6 秒无有效帧 = 连接实质失效或波特率错误 */
  static get DATA_TIMEOUT_MS() {
    return 6000;
  }

  /**
   * 检测浏览器是否支持 Web Serial API
   * @returns {boolean}
   */
  static isSupported() {
    return "serial" in navigator;
  }

  /**
   * 获取连接状态
   * @returns {boolean}
   */
  isConnected() {
    return this._connected;
  }

  /**
   * 连接地磅（弹出设备选择 + 打开串口）
   * 必须由用户手势触发（如按钮点击），否则浏览器会拒绝 requestPort
   *
   * @param {Object} options - 串口配置，可覆盖默认值
   * @param {number} [options.baudRate=9600] - 波特率
   * @param {number} [options.dataBits=8] - 数据位
   * @param {number} [options.stopBits=1] - 停止位
   * @param {string} [options.parity='none'] - 校验位
   * @param {number} [options.bufferSize=2048] - 缓冲区大小
   * @param {string} [options.flowControl='none'] - 流控制
   * @param {SerialPort} [options.presetPort] - 已授权的串口实例
   *        传入时跳过 requestPort 弹窗 (用于页面 mounted 自动连接).
   * @returns {Promise<boolean>} 是否连接成功
   */
  async connect(options = {}) {
    // 检查浏览器支持
    if (!WeighbridgeReader.isSupported()) {
      const msg =
        "您的浏览器不支持 Web Serial API，请使用 Chrome 89+ 或 Edge 89+";
      this._notifyError(msg);
      return false;
    }

    // 已连接则先断开
    if (this._connected) {
      await this.disconnect();
    }

    // 提取 presetPort / protocol 后再透传到 open()
    const { presetPort, protocol, ...openConfig } = options;
    if (protocol && typeof protocol === "string") {
      this._protocol = protocol;
    }

    try {
      // 优先用预设端口 (静默自动连接), 否则弹设备选择对话框
      if (presetPort) {
        this.serialPort = presetPort;
      } else {
        this.serialPort = await navigator.serial.requestPort();
      }

      // 合并配置
      const config = { ...this._defaultOptions, ...openConfig };

      // [2026-08-29] 按协议套用串口参数差异: PT650D 串口是 7-E-1(偶校验),
      // 其余协议保持默认 8-N-1。调用方显式传的 dataBits/parity 优先。
      const protoSerial = SCALE_PROTOCOL_SERIAL_PARAMS[this._protocol];
      if (protoSerial) {
        if (openConfig.dataBits === undefined) config.dataBits = protoSerial.dataBits;
        if (openConfig.parity === undefined) config.parity = protoSerial.parity;
      }

      // [2026-07-15] 端口残留 open 修复: 刷新页面/异常退出后, 浏览器可能仍认为该 SerialPort 处于
      //   open 状态 (InvalidStateError). 自动连接 (presetPort) 场景下, 先 close 再 reopen 恢复,
      //   而不是直接放弃 (原代码静默 return false 导致读不出数, 且残留 open 永远关不掉).
      //   手动连接 (requestPort) 路径保留原行为: 这种场景浏览器一般不会给残留 open 的端口.
      try {
        await this._openPortSafe(config);
      } catch (openErr) {
        if (
          (openErr.name === "InvalidStateError" && presetPort) ||
          openErr.name === "NotFoundError"
        ) {
          throw openErr; // 残留 open 兜底已失败 / 用户取消, 交给外层处理
        }
        console.warn("[WeighbridgeReader] open 失败, 600ms 后重试一次:", openErr.message);
        await new Promise((r) => setTimeout(r, 600));
        await this._openPortSafe(config);
      }

      this.serialOpen = true;
      this._connected = true;

      console.log(
        `[WeighbridgeReader] 串口已打开: 波特率=${config.baudRate}, 数据位=${config.dataBits}, 停止位=${config.stopBits}, 校验=${config.parity}`,
      );

      // 触发连接成功回调
      if (this.onConnect) {
        this.onConnect();
      }

      // 开始读取数据
      this._readSerialData();

      // [2026-07-24] 启动有效帧超时 watchdog + 重置诊断标记
      this._rawDataReceived = false;
      this._startDataWatchdog();

      return true;
    } catch (error) {
      // 用户取消选择设备不报错
      if (error.name === "NotFoundError") {
        console.log("[WeighbridgeReader] 用户取消了设备选择");
        return false;
      }
      // [2026-07-15] 残留 open 的兜底已在 _openPortSafe 内尝试过 (close + reopen),
      //   走到这里说明恢复也失败了 — 按错误类型给友好提示. (原"跳过重连静默 return false"已废弃,
      //   那是导致刷新后读不出数、且残留 open 永远关不掉的直接原因.)
      // [2026-08-02] 统一用 friendlySerialError 转译底层英文报错为中文提示.
      const msg = friendlySerialError(error);
      console.error(`[WeighbridgeReader] ${msg}`, error);
      this._notifyError(msg);
      return false;
    }
  }

  /**
   * 打开串口, 内部处理"残留 open"恢复.
   * 页面刷新/异常退出后, 浏览器可能仍认为该 SerialPort 处于 open 状态 (open() 抛 InvalidStateError).
   * 此时先 close 把残留状态清掉, 再重新 open. 仅对预设端口 (自动连接) 启用恢复, 因为手动选择
   * 的端口一般不会有残留 open; 且手动场景浏览器不允许这样操作.
   * @param {Object} config - 透传给 serialPort.open 的配置
   * @param {boolean} [recover=true] - 是否允许一次 close+reopen 恢复
   */
  async _openPortSafe(config, recover = true) {
    try {
      await this.serialPort.open(config);
    } catch (openErr) {
      // 端口已 open (残留状态) — 自动连接场景下尝试恢复: close 再 reopen
      if (openErr.name === "InvalidStateError" && recover) {
        console.warn("[WeighbridgeReader] 端口残留 open, 尝试 close 后 reopen:", openErr.message);
        try {
          await this.serialPort.close();
        } catch (closeErr) {
          console.warn("[WeighbridgeReader] 残留 open 的 close 失败:", closeErr.message);
        }
        // close 后重置去重标记, 让后续正常流程能再 close 一次
        this._portClosed = false;
        await this.serialPort.open(config); // 再失败就抛给外层
        console.log("[WeighbridgeReader] 残留 open 已恢复, 串口重新打开成功");
        return;
      }
      throw openErr; // 其它错误 (NotFound 等) 直接抛
    }
  }

  /**
   * 关闭串口 (带去重): 同一个实例生命周期内只真正 close 一次.
   * 解决 destroy() 的 close 与 _readSerialData() 尾部的 close 并发执行的问题 —
   * MOXA 驱动层并发 close 会把 COM 口句柄卡在"半释放"状态, 导致刷新后 reopen 失败.
   */
  async _closePortOnce() {
    if (!this.serialPort || this._portClosed) return;
    this._portClosed = true;
    try {
      await this.serialPort.close();
    } catch (e) {
      // 可能已关闭, 忽略
    }
  }

  /**
   * 断开连接
   * 取消 reader，串口会在读取循环结束后自动关闭
   */
  async disconnect() {
    this.serialOpen = false;
    this._connected = false;

    // [2026-07-24] 停止数据超时 watchdog
    this._stopDataWatchdog();

    // 清理分包合并定时器
    if (this.serialMergeTimer) {
      clearTimeout(this.serialMergeTimer);
      this.serialMergeTimer = null;
    }

    // 清空缓冲区
    this.serialDataBuffer = [];
    this.recentWeights = [];

    // 取消 reader，触发 _readSerialData 中的循环退出
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (e) {
        // reader 可能已被释放，忽略错误
      }
    }

    // [2026-07-15] 等待读取循环真正退出 (带超时保护), 确保后续 close 不会与之并发.
    //   原来这里只 cancel reader 就返回, destroy() 的 close 与 _readSerialData() 尾部的 close 并发,
    //   MOXA 驱动句柄会卡死半释放状态, 刷新后 reopen 失败.
    if (this._readLoopDeferred) {
      await Promise.race([
        this._readLoopDeferred.promise,
        new Promise((r) => setTimeout(r, 2000)),
      ]);
    }

    console.log("[WeighbridgeReader] 已断开连接");

    // 触发断开回调
    if (this.onDisconnect) {
      this.onDisconnect();
    }
  }

  /**
   * 完全销毁实例
   * 断开连接并清理所有资源，之后不可再使用
   *
   * [2026-07-15] close 去重: disconnect() 已等读取循环退出并由 _readSerialData 走 _closePortOnce 关闭,
   *   这里再调一次 _closePortOnce 是幂等的 (去重标记保证只 close 一次), 不会与读取循环尾部并发.
   */
  async destroy() {
    await this.disconnect();

    // 关闭串口 (幂等, 与 _readSerialData 尾部共用 _closePortOnce 去重)
    await this._closePortOnce();
    this.serialPort = null;

    // 清除所有回调
    this.onWeightChange = null;
    this.onConnect = null;
    this.onDisconnect = null;
    this.onError = null;
    this.onRawData = null;
    this.onDataTimeout = null;

    console.log("[WeighbridgeReader] 实例已销毁");
  }

  /**
   * 设置重量变化回调
   * @param {Function} callback - (weight: number, stable: boolean) => void
   */
  setOnWeightChange(callback) {
    this.onWeightChange = callback;
  }

  /**
   * 设置连接成功回调
   * @param {Function} callback - () => void
   */
  setOnConnect(callback) {
    this.onConnect = callback;
  }

  /**
   * 设置断开回调
   * @param {Function} callback - () => void
   */
  setOnDisconnect(callback) {
    this.onDisconnect = callback;
  }

  /**
   * 设置错误回调
   * @param {Function} callback - (msg: string) => void
   */
  setOnError(callback) {
    this.onError = callback;
  }

  /**
   * 设置原始数据回调（用于调试）
   * @param {Function} callback - (hexStr: string, textStr: string) => void
   */
  setOnRawData(callback) {
    this.onRawData = callback;
  }

  /**
   * [2026-07-24] 设置数据超时回调 (连接后长时间无法解析出有效帧时触发).
   * 回调参数: ({rawDataReceived: boolean}) => void
   *   rawDataReceived=false: 完全无数据 (端口降级/断开) → 上层应重连
   *   rawDataReceived=true:  有数据但解析失败 (波特率不匹配) → 上层应自动探测波特率
   */
  setOnDataTimeout(callback) {
    this.onDataTimeout = callback;
  }

  // ==================== 内部方法 ====================

  /**
   * [2026-07-24] 启动有效帧超时 watchdog.
   * 耀华连续模式(tF=0)的地磅每 ~100ms 发送一帧, 正常运行时不可能 6 秒解析不出一帧.
   * 超时后带诊断信息触发 onDataTimeout, 上层据此决定:
   *   - 零原始数据 → 端口降级 → 完整断开+重连
   *   - 有原始数据但无有效帧 → 波特率错误 → 自动切换波特率探测
   */
  _startDataWatchdog() {
    this._stopDataWatchdog();
    this._lastFrameAt = Date.now();
    this._dataWatchdogTimer = setInterval(() => {
      if (!this._connected || !this.serialOpen) {
        this._stopDataWatchdog();
        return;
      }
      if (Date.now() - this._lastFrameAt > WeighbridgeReader.DATA_TIMEOUT_MS) {
        console.warn(
          `[WeighbridgeReader] ${WeighbridgeReader.DATA_TIMEOUT_MS}ms 内无有效帧 (rawDataReceived=${this._rawDataReceived}), 触发 onDataTimeout`,
        );
        this._stopDataWatchdog();
        if (this.onDataTimeout) {
          this.onDataTimeout({ rawDataReceived: this._rawDataReceived });
        }
      }
    }, 2000);
  }

  /** [2026-07-24] 停止数据超时 watchdog */
  _stopDataWatchdog() {
    if (this._dataWatchdogTimer) {
      clearInterval(this._dataWatchdogTimer);
      this._dataWatchdogTimer = null;
    }
  }

  /** [2026-07-24] 解析出有效帧时喂狗 (重置超时计时) */
  _feedDataWatchdog() {
    this._lastFrameAt = Date.now();
  }

  /**
   * 循环读取串口数据
   * 采用直接 getReader 方式，在 finally 中 releaseLock
   * 循环结束后通过 _closePortOnce 关闭串口 (幂等, 与 destroy 共用去重), 并 resolve _readLoopDeferred.
   *
   * [2026-07-15] 关键修复:
   *   - _readLoopDeferred: 循环退出时 resolve, disconnect() await 它, 保证"读取循环退出"与"关闭串口"
   *     串行执行, 不再并发 close.
   *   - _closePortOnce: 取代原来的裸 serialPort.close(), 保证整个生命周期只 close 一次.
   *     不再在这里把 serialPort 置 null (destroy 统一管), 避免 disconnect 等待后 serialPort 已 null 的混乱.
   */
  async _readSerialData() {
    console.log("[WeighbridgeReader] 开始读取数据...");
    // 新建 deferred, disconnect() 会 await 它直到本循环退出
    this._readLoopDeferred = { promise: null, resolve: null };
    this._readLoopDeferred.promise = new Promise(
      (r) => (this._readLoopDeferred.resolve = r),
    );

    while (this.serialOpen && this.serialPort && this.serialPort.readable) {
      this.reader = this.serialPort.readable.getReader();
      try {
        while (true) {
          const { value, done } = await this.reader.read();
          if (done) {
            break;
          }
          if (value) {
            this._dataReceived(value);
          }
        }
      } catch (error) {
        if (this.serialOpen) {
          console.warn(`[WeighbridgeReader] 读取数据错误: ${error.message}`);
          // [2026-08-02] 读取阶段错误也走友好转译, 不裸弹英文
          this._notifyError(`读取数据时出错: ${friendlySerialError(error)}`);
        }
      } finally {
        this.reader.releaseLock();
        this.reader = null;
      }
    }

    // 循环结束后关闭串口 (幂等)
    await this._closePortOnce();

    this._connected = false;
    this.serialOpen = false;

    // 通知 disconnect(): 读取循环已退出, 串口已关闭
    if (this._readLoopDeferred) {
      this._readLoopDeferred.resolve();
      this._readLoopDeferred = null;
    }
  }

  /**
   * 串口分包合并 + 按帧切分
   * 串口数据可能分多次到达，用定时器合并 50ms 内的数据；
   * 合并后按 STX(0x02)/ETX(0x03) 切出所有完整帧，残片留在缓冲区等下次。
   * @param {Uint8Array} rawData - 原始字节数据
   */
  _dataReceived(rawData) {
    // [2026-07-24] 记录"收到过原始数据" (watchdog 诊断用: 区分端口降级 vs 波特率错误)
    this._rawDataReceived = true;
    this.serialDataBuffer.push(...rawData);

    // 清除之前的合并定时器
    if (this.serialMergeTimer) {
      clearTimeout(this.serialMergeTimer);
    }

    this.serialMergeTimer = setTimeout(() => {
      if (this.serialDataBuffer.length === 0) return;
      this.serialMergeTimer = null;

      // 调试用：原始合并字节(切帧前的整段)
      if (this.onRawData) {
        const mergedForDebug = new Uint8Array(this.serialDataBuffer);
        const hexStr = Array.from(mergedForDebug)
          .map((b) => ("0" + b.toString(16).toUpperCase()).slice(-2))
          .join(" ");
        this.onRawData(hexStr, this.textDecoder.decode(mergedForDebug));
      }

      // 按协议注册表切出完整帧, 残片留在 serialDataBuffer
      const handler =
        SCALE_PROTOCOL_PARSE_HANDLERS[this._protocol] ||
        SCALE_PROTOCOL_PARSE_HANDLERS["yaohua-continuous"];
      const frames = handler
        ? handler.split(this.serialDataBuffer)
        : splitYaohuaFrames(this.serialDataBuffer);
      frames.forEach((frame) => this._processFrame(frame));
    }, this.mergeTimeout);
  }

  /**
   * [2026-07-26] 桌面专用串口：接收 webbox IPC 推送的原始字节，喂给解析流程
   * 与 _dataReceived 共享分包合并 + 帧切分 + 解析逻辑，区别是数据来源：
   *   _dataReceived: navigator.serial 读取循环（浏览器/Web Serial 路径）
   *   feedData:      webbox IPC serial:data 事件（桌面 Node serialport 路径）
   *
   * 用法（桌面模式不调 connect，只调 setOnWeightChange + feedData + startWatchdog）：
   *   reader.setOnWeightChange((w, s) => { ... })
   *   reader._startDesktopWatchdog()  // 启动超时检测
   *   window.desktop.serial.onData(payload => reader.feedData(hexToBytes(payload.hex)))
   *
   * @param {Uint8Array|number[]} rawData - 原始字节数据
   */
  feedData(rawData) {
    this._dataReceived(rawData);
  }

  /**
   * [2026-07-26] 桌面模式启动数据 watchdog（复用 connect 里的超时检测，但不走 navigator.serial）
   */
  startDesktopWatchdog() {
    this.serialOpen = true;      // 让 _feedDataWatchdog 认为连接中
    this._connected = true;
    this._feedDataWatchdog();    // 启动定时器
  }

  /**
   * 处理一帧完整数据
   * @param {number[]} frame - 含 STX 和 ETX 的完整帧字节数组
   */
  _processFrame(frame) {
    try {
      const weight = this._parseFrame(frame);
      if (weight === null) return;
      // [2026-07-24] 解析出有效帧 → 喂狗 (重置 watchdog 超时)
      this._feedDataWatchdog();
      // [合理性校验] 现场电磁干扰偶发会出一帧"合法但荒谬"的值 (几百万吨 / 极端负值),
      // 在源头丢弃, 防止脏数据写入 store 进而进单据. 重置稳定性窗口避免历史污染.
      if (!this._isPlausibleWeight(weight)) {
        this.recentWeights = [];
        return;
      }
      const stable = this._isStable(weight);
      if (this.onWeightChange) {
        this.onWeightChange(weight, stable);
      }
    } catch (error) {
      console.error(`[WeighbridgeReader] 数据处理错误: ${error.message}`);
    }
  }

  /**
   * 重量合理性校验: 丢弃明显荒谬的绝对值 (帧格式合法但数值离谱).
   * 覆盖任何地磅量程 (常见 30~150 吨); 现场车+粮毛重极少超 200 吨.
   * 保留合理小负值 (地磅归零时 ±几 的漂移), 只挡极端值.
   * @param {number} weight
   * @returns {boolean}
   */
  _isPlausibleWeight(weight) {
    return isPlausibleWeightValue(weight);
  }

  /**
   * 解析一帧完整数据(按 this._protocol 分发解析器)
   * @param {number[]|Uint8Array} frame - 含 STX 和 ETX 的完整帧
   * @returns {number|null} 真实重量(已按小数位换算)，失败返回 null
   */
  _parseFrame(frame) {
    // 按协议注册表分发; 未注册协议兜底走耀华连续(最常见), 避免完全读不出
    const handler =
      SCALE_PROTOCOL_PARSE_HANDLERS[this._protocol] ||
      SCALE_PROTOCOL_PARSE_HANDLERS["yaohua-continuous"];
    if (!handler) return parseYaohuaFrame(frame);
    return handler.parse(frame);
  }

  /**
   * 判断重量是否稳定
   * 最近 3 次读数相同则认为稳定
   * @param {number} weight - 当前重量
   * @returns {boolean}
   */
  _isStable(weight) {
    this.recentWeights.push(weight);
    if (this.recentWeights.length > 5) {
      this.recentWeights.shift();
    }

    if (this.recentWeights.length < 3) {
      return false;
    }

    const last3 = this.recentWeights.slice(-3);
    return last3.every((w) => w === last3[0]);
  }

  /**
   * 通知错误
   * @param {string} msg - 错误信息
   */
  _notifyError(msg) {
    console.error(`[WeighbridgeReader] ${msg}`);
    if (this.onError) {
      this.onError(msg);
    }
  }
}

// ==================== 串口错误友好转译 ====================
//
// 背景: 串口底层报错(无论桌面 Node serialport 还是浏览器 Web Serial)都是英文技术细节,
//   形如 "打开 COM1 失败: Opening COM1: Access denied" / "Failed to open serial port".
//   直接弹给用户是一串看不懂的英文. 这里把常见错误按场景分类, 统一转成中文友好提示.
//
// 调用方(三处共用同一套文案, 单一来源):
//   1. weighbridgeMixin.js — 桌面连接 catch / Web Serial onError / 桌面 onError 回调
//   2. weighbridgeReader.js — connect catch / 读取循环错误
// 主进程 serial-manager.js 保持抛原始错误, 中文翻译统一在这里做.

/**
 * 把串口底层错误(Error 或字符串)转译成用户能看懂的中文提示.
 * 已知错误按场景给具体解决办法; 未知错误给中文兜底 + 括号附原文(方便排查).
 * @param {Error|string} e
 * @returns {string}
 */
export function friendlySerialError(e) {
  const raw = String((e && e.message) || e || "").trim();

  // 1) 串口被占用 / 拒绝访问
  //    桌面 serialport: "Access denied" / "Permission denied" / EACCES / EPERM
  //    Web Serial: "Failed to open" / InvalidStateError(残留 open)
  if (
    /access denied|permission denied|EACCES|EPERM|busy|in use|拒绝访问|被占用/i.test(raw) ||
    (e && e.name === "InvalidStateError") ||
    /Failed to open/i.test(raw)
  ) {
    return "串口被占用或没有权限。可能是其它程序(如串口调试助手、其它过磅软件)正开着这个口，或上次连接没正常释放。请关闭占用该串口的程序、或拔插一下 USB 转串口线后再点连接。";
  }

  // 2) 端口不存在 / 找不到 (COM 号错了或没插)
  if (/not found|file not found|does not exist|ENOENT|no devices|找不到|不存在/i.test(raw)) {
    return "找不到这个串口。请检查 USB 转串口线是否插好，并在「系统管理→过磅设备」里确认 COM 端口号是否正确。";
  }

  // 3) 模块 / 驱动未加载 (桌面环境 serialport 原生模块没装好)
  if (/serialport 模块未加载|Cannot find module|native module|\.node 绑定/i.test(raw)) {
    return "桌面串口驱动未正常加载。请重启程序；若仍不行，请联系运维重新安装桌面端。";
  }

  // 4) 参数缺失 (comPort / baudRate 没传或非法)
  if (/缺少 comPort|comPort 参数|Invalid argument|无效参数/i.test(raw)) {
    return "地磅串口配置有误。请在「系统管理→过磅设备」里检查 COM 口和波特率设置。";
  }

  // 5) 设备断开 / 拔出 (运行中物理断开)
  if (/disconnected|device disconnected|拔出|已断开|设备未连接|ECONNRESET/i.test(raw)) {
    return "地磅连接已断开。请检查串口线是否松动、地磅是否通电，然后重新点连接。";
  }

  // 6) 远程调用失败 (Electron IPC 通信超时/失败)
  if (/Error invoking remote method|timed out|timeout|ETIMEDOUT|ECONNREFUSED/i.test(raw)) {
    return "与本地服务通信失败。请重启程序后再试；若反复出现请联系运维。";
  }

  // 7) 未知错误兜底: 中文提示 + 括号附原文(方便排查, 不裸弹英文)
  return `连接失败，请检查地磅设备后重试。（${raw || "未知错误"}）`;
}

// ==================== 波特率自动探测 ====================
//
// 背景: 地磅仪表的波特率现场不一致(常见 1200/2400/4800/9600), 配错能 open 但读不出有效帧.
// 本函数对同一个已授权 SerialPort 逐档试波特率, 以"能否在超时窗口内解析出至少一帧合理重量"为
// 判据(parseYaohuaFrame 校验通过 + _isPlausibleWeight), 命中即返回该波特率.
//
// 为什么用 connect/disconnect 而非裸 open/read: disconnect() 会 await 读取循环退出并 _closePortOnce
// 真正关闭串口, 之后同一 SerialPort 才能被下一档重新 open; 每档用全新实例避开 _portClosed 去重
// 与 MOXA 驱动并发 close 句柄卡死的风险(见类内 2026-07-15 注释).
//
// 注意: 必须在用户手势内调用(首次需 requestPort 弹窗授权), 探测本身用 presetPort 不再弹窗.

/**
 * 对已授权的串口逐档试探波特率, 找到能读出有效重量帧的那一档.
 *
 * @param {SerialPort} serialPort - 已授权的 Web Serial 端口实例(不能是 null)
 * @param {string} [protocol='yaohua-continuous'] - 地磅协议, 透传给 reader
 * @param {number[]} [candidateBauds=[9600,4800,2400,1200]] - 候选波特率(按优先级, 最常见放前)
 * @param {Object} [opts]
 * @param {number} [opts.timeout=3000] - 每档等待有效帧的超时(毫秒)
 * @param {Function} [opts.onProgress] - 进度回调 (baud:number, ok:boolean) => void
 * @returns {Promise<number|null>} 命中的波特率; 全部失败返回 null
 */
export async function probeBaudRate(serialPort, protocol, candidateBauds, opts = {}) {
  if (!serialPort) return null;
  const bauds = Array.isArray(candidateBauds) && candidateBauds.length
    ? candidateBauds.slice()
    : [9600, 4800, 2400, 1200];
  const timeout = Number(opts.timeout) > 0 ? Number(opts.timeout) : 3000;
  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;

  for (const baud of bauds) {
    // 每档全新实例: 避开 _portClosed/_readLoopDeferred/_connected 等实例级状态
    const reader = new WeighbridgeReader();
    let gotFrame = false;
    let frameResolver = null;
    const framePromise = new Promise((r) => { frameResolver = r; });

    reader.setOnWeightChange(() => {
      // 读到一帧合理重量即认为波特率正确(stable 与否无所谓, 首帧就够)
      if (!gotFrame) {
        gotFrame = true;
        if (frameResolver) frameResolver(true);
      }
    });

    let opened = false;
    try {
      opened = await reader.connect({ presetPort: serialPort, baudRate: baud, protocol });
    } catch (e) {
      opened = false;
    }

    if (!opened) {
      // open 失败(端口占用/异常) → 直接试下一档
      try { await reader.destroy(); } catch (e) { /* 忽略 */ }
      if (onProgress) onProgress(baud, false);
      continue;
    }

    // 等待有效帧 或 超时
    await Promise.race([
      framePromise,
      new Promise((r) => setTimeout(r, timeout)),
    ]);

    // disconnect: await 读取循环退出 + 关口, 保证下一档能重新 open
    try { await reader.destroy(); } catch (e) { /* 忽略 */ }

    if (gotFrame) {
      if (onProgress) onProgress(baud, true);
      return baud;
    }
    if (onProgress) onProgress(baud, false);
  }
  return null;
}

// ==================== 协议自动检测(Web Serial 版) ====================
//
// 对已授权的串口逐档波特率抓原始字节(只收不解), 每档抓 duration 毫秒后用
// detectProtocolFromBuffer 离线跑全部协议解析器, 命中即返回 {baudRate, protocol, weight}。
// "耀华优先"在 detectProtocolFromBuffer 内部体现(同一份字节流先试耀华再试其它协议)。
// 必须在用户手势内调用(首次需 requestPort 弹窗授权), 探测用 presetPort 不再弹窗。

/**
 * 用指定波特率打开串口并抓取 duration 毫秒原始字节(只收不解)。
 * @param {SerialPort} serialPort - 已授权的 Web Serial 端口实例
 * @param {number} baudRate
 * @param {number} durationMs - 抓取时长
 * @returns {Promise<number[]|null>} 抓到的字节(空数组=打开成功但没数据); open 失败返回 null
 */
export async function captureRawBytes(serialPort, baudRate, durationMs) {
  if (!serialPort) return null;
  const reader = new WeighbridgeReader();
  const hexParts = [];
  reader.onRawData = (hexStr) => {
    if (hexStr) hexParts.push(hexStr);
  };
  let opened = false;
  try {
    opened = await reader.connect({ presetPort: serialPort, baudRate });
  } catch (e) {
    opened = false;
  }
  if (!opened) {
    try { await reader.destroy(); } catch (e) { /* 忽略 */ }
    return null;
  }
  await new Promise((r) => setTimeout(r, Math.max(200, durationMs)));
  try { await reader.destroy(); } catch (e) { /* 忽略 */ }
  const bytes = [];
  for (const part of hexParts) {
    for (const tok of String(part).split(" ")) {
      const v = parseInt(tok, 16);
      if (!isNaN(v)) bytes.push(v);
    }
  }
  return bytes;
}

/**
 * 对已授权串口做协议检测: 逐档波特率抓字节 → 离线全协议匹配(耀华优先), 命中即停。
 * @param {SerialPort} serialPort
 * @param {number[]} [candidateBauds=[9600,4800,2400,1200]] - 候选波特率(最常见在前)
 * @param {Object} [opts]
 * @param {number} [opts.timeout=2500] - 每档抓取时长(毫秒)
 * @param {Function} [opts.onProgress] - 进度回调 (step:{baud, bytes, detect, error}) => void
 * @returns {Promise<{baudRate:number, protocol:string|null, weight:number|null, tried:Array}>}
 *          命中: baudRate>0 且 protocol 非空; 全部失败: baudRate=0, protocol=null
 */
export async function detectProtocolOnPort(serialPort, candidateBauds, opts = {}) {
  const bauds =
    Array.isArray(candidateBauds) && candidateBauds.length
      ? candidateBauds.slice()
      : [9600, 4800, 2400, 1200];
  const timeout = Number(opts.timeout) > 0 ? Number(opts.timeout) : 2500;
  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;
  const tried = [];

  for (const baud of bauds) {
    let bytes = null;
    let error = "";
    try {
      bytes = await captureRawBytes(serialPort, baud, timeout);
    } catch (e) {
      bytes = null;
      error = (e && e.message) || String(e);
    }
    const detect = bytes && bytes.length ? detectProtocolFromBuffer(bytes) : null;
    const step = {
      baud,
      bytes: bytes ? bytes.length : 0,
      rawHex: bytes
        ? bytes.map((v) => ("0" + v.toString(16).toUpperCase()).slice(-2)).join(" ")
        : "",
      detect,
      error,
    };
    tried.push(step);
    if (onProgress) onProgress(step);
    if (detect) {
      return { baudRate: baud, protocol: detect.protocol, weight: detect.weight, tried };
    }
  }
  return { baudRate: 0, protocol: null, weight: null, tried };
}

export default WeighbridgeReader;
